(() => {
  const TERMLY_WAIT_MS = 10000;
  const TERMLY_CACHE_KEY = 'TERMLY_API_CACHE';
  const TERMLY_CONSENT_KEY = 'TERMLY_COOKIE_CONSENT';
  const TRACKING_SRC = 'https://cdn.livechatinc.com/tracking.js';
  const LIVECHAT_RE = /livechatinc\.com|cdn\.livechatinc\.com/i;
  const LIVECHAT_SCRIPT_SELECTOR =
    'script[src*="livechatinc.com"], script[src*="cdn.livechatinc.com"], script[data-src*="livechatinc.com"]';
  const LIVECHAT_WIDGET_SELECTOR =
    '#chat-widget-container, #chat-widget-minimized, [id^="chat-widget"], iframe[src*="livechatinc.com"], iframe[src*="cdn.livechatinc.com"]';
  const LIVECHAT_COOKIE_RE = /^(__lc_|__livechat|lc_window|lc_invitation|lc_sso|CASID$)/i;
  const LIVECHAT_STORAGE_RE = /^(?:__lc_|__livechat|lc_)/i;

  let trackingLoaded = false;
  let lastAllowed = null;
  let termlyConsentMode = null;
  let termlyEventDecision = null;

  const isDesignMode = () => document.documentElement.classList.contains('shopify-design-mode');

  const termlyExpected = () =>
    Boolean(window.Termly || document.querySelector('script[src*="app.termly.io"]'));

  // Termly.getConsentState() is getConsentSettings() || getDefaultConsents().
  // In opt_out regions the defaults are category=true (implied grant) before any click.
  const termlyStoredConsents = () => {
    try {
      const raw = window.localStorage.getItem(TERMLY_CACHE_KEY);
      if (!raw) return null;
      const entry = JSON.parse(raw)[TERMLY_CONSENT_KEY];
      if (!entry || entry.value == null || typeof entry.value !== 'object') return null;
      return entry.value;
    } catch (error) {
      return null;
    }
  };

  const termlyHasExplicitDecision = () => {
    if (termlyEventDecision && typeof termlyEventDecision === 'object') return true;
    return termlyStoredConsents() != null;
  };

  const rememberTermlyDecision = (data) => {
    if (!data || typeof data !== 'object') return;
    if (data.consentState && typeof data.consentState === 'object') {
      termlyEventDecision = data.consentState;
    }
  };

  const termlyState = () => {
    if (!termlyExpected()) return null;
    if (!window.Termly || typeof window.Termly.getConsentState !== 'function') {
      return {
        ready: false,
        analytics: false,
        marketing: false,
        explicit: termlyHasExplicitDecision(),
        consentMode: termlyConsentMode,
      };
    }
    const state = window.Termly.getConsentState() || {};
    return {
      ready: true,
      analytics: state.analytics === true,
      marketing: state.advertising === true,
      explicit: termlyHasExplicitDecision(),
      consentMode: termlyConsentMode,
    };
  };

  const shopifyState = () => {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return null;
    return {
      ready: true,
      analytics: privacy.analyticsProcessingAllowed() === true,
      marketing: typeof privacy.marketingAllowed === 'function' ? privacy.marketingAllowed() === true : false,
      saleOfData: typeof privacy.saleOfDataAllowed === 'function' ? privacy.saleOfDataAllowed() === true : true,
    };
  };

  const livechatAllowed = () => {
    if (isDesignMode()) return true;

    const termly = termlyState();
    const shopify = shopifyState();

    if (termly && termly.ready === false) return false;
    if (!termly && !shopify) return false;

    // GPC / do-not-sell when Shopify surfaces it — LiveChat is third-party tracking.
    if (shopify && shopify.saleOfData === false) return false;

    // Decline / GPC: categories false means LiveChat stays off.
    if (termly && termly.ready && !termly.analytics && !termly.marketing) return false;
    if (shopify && !shopify.analytics && !shopify.marketing) return false;

    // Termly present: implied opt_out defaults (category=true, no decision yet) are not consent.
    if (termly) {
      if (!termly.ready || !termly.explicit) return false;
      return Boolean(termly.analytics || termly.marketing);
    }

    return Boolean(shopify && (shopify.analytics || shopify.marketing));
  };

  const rememberScriptUrl = (url) => {
    const value = String(url || '');
    if (!LIVECHAT_RE.test(value)) return;
    window.__tileclubLivechatScripts = window.__tileclubLivechatScripts || [];
    if (window.__tileclubLivechatScripts.indexOf(value) === -1) {
      window.__tileclubLivechatScripts.push(value);
    }
  };

  const captureCustomScripts = () => {
    if (!Array.isArray(window.custom_scripts)) return;
    window.custom_scripts = window.custom_scripts.filter((url) => {
      if (LIVECHAT_RE.test(String(url))) {
        rememberScriptUrl(url);
        return false;
      }
      return true;
    });
  };

  const heldScriptUrls = () => (Array.isArray(window.__tileclubLivechatScripts) ? window.__tileclubLivechatScripts.slice() : []);

  const injectScript = (src) => {
    if (!src) return null;
    const existing = Array.from(document.querySelectorAll('script[src]')).find((script) => {
      const current = script.getAttribute('src') || '';
      return current === src || current.indexOf(src) !== -1 || src.indexOf(current) !== -1;
    });
    if (existing) {
      existing.dataset.livechatConsent = 'allow';
      return existing;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.livechatConsent = 'allow';
    document.head.appendChild(script);
    return script;
  };

  const initWidget = () => {
    try {
      if (window.LiveChatWidget && typeof window.LiveChatWidget.init === 'function') {
        window.LiveChatWidget.init();
      }
    } catch (error) {
      // Widget may already be initializing.
    }
  };

  const ensureTrackingJs = () => {
    if (document.querySelector('script[src*="cdn.livechatinc.com/tracking.js"]')) return;
    if (!window.__lc || !window.__lc.license) return;
    injectScript(TRACKING_SRC);
  };

  const loadTracking = () => {
    if (trackingLoaded) return;

    window.__lc = window.__lc || {};
    // Keep asyncInit so a late Shopify widget.js cannot auto-boot tracking.js.
    // We call LiveChatWidget.init() after the Shopify app script is in place.
    window.__lc.asyncInit = true;

    const urls = heldScriptUrls();
    if (!urls.length && window.__lc.license) {
      urls.push(TRACKING_SRC);
    }

    if (!urls.length) {
      trackingLoaded = true;
      initWidget();
      ensureTrackingJs();
      return;
    }

    let remaining = urls.length;
    const done = () => {
      remaining -= 1;
      if (remaining > 0) return;
      trackingLoaded = true;
      initWidget();
      ensureTrackingJs();
    };

    urls.forEach((url) => {
      const script = injectScript(url);
      if (script && script.src && !script.dataset.livechatLoadBound) {
        script.dataset.livechatLoadBound = 'true';
        script.addEventListener('load', done, { once: true });
        script.addEventListener('error', done, { once: true });
      } else {
        done();
      }
    });
  };

  const clearLivechatCookies = () => {
    const host = window.location.hostname;
    const root = host.split('.').slice(-2).join('.');
    const domains = [host, '.' + host, root, '.' + root, ''];

    document.cookie.split(';').forEach((part) => {
      const name = part.split('=')[0].trim();
      if (!name || !LIVECHAT_COOKIE_RE.test(name)) return;
      domains.forEach((domain) => {
        const domainAttr = domain ? '; domain=' + domain : '';
        document.cookie = name + '=; Max-Age=0; path=/' + domainAttr;
      });
    });

    try {
      Object.keys(window.localStorage).forEach((key) => {
        if (LIVECHAT_STORAGE_RE.test(key)) window.localStorage.removeItem(key);
      });
    } catch (error) {
      // Private mode / blocked storage.
    }

    try {
      Object.keys(window.sessionStorage).forEach((key) => {
        if (LIVECHAT_STORAGE_RE.test(key)) window.sessionStorage.removeItem(key);
      });
    } catch (error) {
      // Private mode / blocked storage.
    }
  };

  const isUnauthorizedLivechatLoader = (node) => {
    if (!node || node.nodeName !== 'SCRIPT') return false;
    if (node.dataset.livechatConsent === 'allow') return false;
    const src = node.getAttribute('src') || node.getAttribute('data-src') || '';
    return LIVECHAT_RE.test(src);
  };

  const removeWidgetDom = () => {
    document.querySelectorAll(LIVECHAT_WIDGET_SELECTOR).forEach((node) => node.remove());
  };

  const stopWidget = () => {
    try {
      if (window.LiveChatWidget && typeof window.LiveChatWidget.call === 'function') {
        window.LiveChatWidget.call('hide');
        window.LiveChatWidget.call('destroy');
      }
    } catch (error) {
      // LiveChat may not have loaded yet.
    }
    try {
      if (window.LC_API && typeof window.LC_API.shutdown === 'function') {
        window.LC_API.shutdown();
      } else if (window.LC_API && typeof window.LC_API.hide_chat_window === 'function') {
        window.LC_API.hide_chat_window();
      }
    } catch (error) {
      // Legacy LiveChat API may be missing.
    }
    try {
      delete window.LiveChatWidget;
    } catch (error) {
      window.LiveChatWidget = undefined;
    }
    try {
      delete window.LC_API;
    } catch (error) {
      window.LC_API = undefined;
    }
  };

  const removeTrackingScripts = () => {
    document.querySelectorAll(LIVECHAT_SCRIPT_SELECTOR).forEach((script) => {
      if (script.dataset.livechatConsent === 'allow' && livechatAllowed()) return;
      const src = script.getAttribute('src') || script.getAttribute('data-src');
      rememberScriptUrl(src);
      script.remove();
    });
    trackingLoaded = false;
  };

  let loaderGuard = null;
  const startLoaderGuard = () => {
    if (loaderGuard) return;
    loaderGuard = new MutationObserver((records) => {
      if (livechatAllowed()) return;
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (isUnauthorizedLivechatLoader(node)) {
            rememberScriptUrl(node.getAttribute('src') || node.getAttribute('data-src'));
            node.remove();
            return;
          }
          if (node.nodeType === 1) {
            const id = node.id || '';
            const src = node.getAttribute && (node.getAttribute('src') || '');
            if (
              id === 'chat-widget-container' ||
              id === 'chat-widget-minimized' ||
              id.indexOf('chat-widget') === 0 ||
              LIVECHAT_RE.test(src)
            ) {
              node.remove();
              return;
            }
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('script').forEach((script) => {
              if (isUnauthorizedLivechatLoader(script)) {
                rememberScriptUrl(script.getAttribute('src') || script.getAttribute('data-src'));
                script.remove();
              }
            });
            node.querySelectorAll(LIVECHAT_WIDGET_SELECTOR).forEach((widget) => widget.remove());
          }
        });
      });
    });
    loaderGuard.observe(document.documentElement, { childList: true, subtree: true });
  };

  const stopLoaderGuard = () => {
    if (loaderGuard) {
      loaderGuard.disconnect();
      loaderGuard = null;
    }
    if (window.__tileclubLivechatEarlyGuard && typeof window.__tileclubLivechatEarlyGuard.disconnect === 'function') {
      window.__tileclubLivechatEarlyGuard.disconnect();
      window.__tileclubLivechatEarlyGuard = null;
    }
  };

  const disableLivechat = () => {
    window.__lc = window.__lc || {};
    window.__lc.asyncInit = true;
    stopWidget();
    removeTrackingScripts();
    removeWidgetDom();
    startLoaderGuard();
    clearLivechatCookies();
  };

  const enableLivechat = () => {
    stopLoaderGuard();
    loadTracking();
  };

  const syncLivechat = () => {
    const allowed = livechatAllowed();
    if (allowed === lastAllowed) return;
    lastAllowed = allowed;
    if (allowed) {
      enableLivechat();
    } else {
      disableLivechat();
    }
  };

  const listenTermly = () => {
    if (!window.Termly || typeof window.Termly.on !== 'function') return;
    window.Termly.on('initialized', (data) => {
      if (data && data.region_settings && data.region_settings.consent_mode) {
        termlyConsentMode = data.region_settings.consent_mode;
      }
      syncLivechat();
    });
    window.Termly.on('consent', (data) => {
      rememberTermlyDecision(data);
      syncLivechat();
    });
  };

  const whenTermlyReady = (callback) => {
    let firstSync = false;
    const runFirstSync = () => {
      if (firstSync) return;
      firstSync = true;
      callback();
    };

    const attach = () => {
      listenTermly();
      runFirstSync();
      syncLivechat();
    };

    if (window.Termly && typeof window.Termly.getConsentState === 'function') {
      attach();
      return;
    }

    if (!termlyExpected()) {
      runFirstSync();
      return;
    }

    const started = Date.now();
    const timer = setInterval(() => {
      if (window.Termly && typeof window.Termly.getConsentState === 'function') {
        clearInterval(timer);
        attach();
      } else if (Date.now() - started > TERMLY_WAIT_MS) {
        clearInterval(timer);
        runFirstSync();
      }
    }, 50);
  };

  const listenShopify = (callback) => {
    document.addEventListener('visitorConsentCollected', syncLivechat);

    if (
      window.Shopify &&
      window.Shopify.customerPrivacy &&
      typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function'
    ) {
      callback();
      return;
    }

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      callback();
      return;
    }

    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
      callback();
    });
  };

  window.__tileclubLivechatConsent = {
    allowed: livechatAllowed,
    sync: syncLivechat,
    debug: () => {
      const termly = termlyState();
      const allowed = livechatAllowed();
      return {
        termly,
        shopify: shopifyState(),
        storedConsents: termlyStoredConsents(),
        allowed,
        trackingLoaded,
        heldScripts: heldScriptUrls(),
        customScripts: Array.isArray(window.custom_scripts) ? window.custom_scripts.slice() : [],
        livechatScriptCount: document.querySelectorAll(LIVECHAT_SCRIPT_SELECTOR).length,
        livechatWidgetCount: document.querySelectorAll(LIVECHAT_WIDGET_SELECTOR).length,
        reason: !allowed
          ? termly && termly.ready && !termly.explicit
            ? 'no-explicit-termly-decision'
            : 'not-allowed'
          : trackingLoaded
            ? 'allowed-and-loaded'
            : 'allowed',
      };
    },
  };

  captureCustomScripts();
  startLoaderGuard();

  whenTermlyReady(() => {
    listenShopify(syncLivechat);
  });
})();
